import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { getAuthToken } from '../../utils/auth';
import { createVirtualBackgroundProcessor } from './virtualBackground';
import { getUserMediaSafe } from '../../utils/getUserMediaSafe';
import { usePeerMediaIssues } from './monitoring/usePeerMediaIssues';

// Simple WebRTC manager for mesh calls (2-10 participants)
// Responsibilities:
// - Manage local media (camera/mic) and screen sharing
// - Maintain RTCPeerConnections per remote participant
// - Handle signaling via Socket.IO namespace /rtc
// - Expose participants map: {id, name, stream}

const DEFAULT_STUN = [
  {urls: 'stun:meet.edumee.vn:3478'}, // Custom STUN server (hostname)
  {urls: 'stun:stun.l.google.com:19302' },
  {urls: 'stun:stun1.l.google.com:19302'},
  {urls: 'stun:stun3.l.google.com:19302'},
];

export function useWebRTC({ roomId, displayName, groupName }) {
  // Normalize an RTCSessionDescriptionInit to ensure SDP ends with a newline.
  function normalizeDescription(desc) {
    try {
      if (desc && typeof desc.sdp === 'string' && !desc.sdp.endsWith('\n')) {
        // Some environments are sensitive to missing terminal newlines in SDP payloads.
        desc = { ...desc, sdp: desc.sdp + '\n' };
      }
    } catch {}
    return desc;
  }

  // iOS/Safari: ensure audio can play after a user gesture
  async function unlockIOSAudio() {
    try {
      const AC = (typeof window !== 'undefined') && (window.AudioContext || window.webkitAudioContext);
      if (AC) {
        window.__ac = window.__ac || new AC();
        if (window.__ac?.state === 'suspended') { try { await window.__ac.resume(); } catch {} }
      }
    } catch {}
    try {
      const els = Array.from(document.querySelectorAll('audio,video'));
      for (const el of els) {
        try { await el.play(); } catch {}
      }
    } catch {}
  }

  const [joined, setJoined] = useState(false);
  const [participants, setParticipants] = useState({}); // id -> { id, name, stream, isScreen }
  const [hostId, setHostId] = useState(null);
  const [selfId, setSelfId] = useState(null);
  const [screenSharerId, setScreenSharerId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [roomGroupName, setRoomGroupName] = useState('');
  const [endedByHost, setEndedByHost] = useState(false);
  // Additional simple flags for compatibility with simpler API expectations
  const [micEnabled, setMicEnabled] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [backgroundEffect, setBackgroundEffectState] = useState('none'); // 'none' | 'blur' | ...

  const localStreamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const peersRef = useRef(new Map()); // remoteId -> RTCPeerConnection
  const vbRef = useRef({ processor: null, outputStream: null });
  const metaRef = useRef(new Map()); // remoteId -> { makingOffer:boolean, polite:boolean }
  const socketRef = useRef(null);
  const lastJoinParamsRef = useRef(null); // remember last join payload for reconnects
  // Desired media states so UI toggles before permissions are respected once media starts
  const desiredMicEnabledRef = useRef(true);
  const desiredCamEnabledRef = useRef(true);
  // Hidden remote sinks for iOS autoplay/ringer mute workaround
  const remoteSinksRef = useRef(new Map()); // remoteId -> HTMLVideoElement
  // Watchdog for outbound audio: detect remoteRtpId staying null persistently
  const audioRemoteWatchRef = useRef({ failures: 0, notified: false });
  // Throttle a one-time rejoin when joined but no peers discovered
  const noPeerRejoinRef = useRef({ attempted: false });
  // Ref wrapper for makeOffer to avoid temporal dead zone in earlier hooks
  const makeOfferRef = useRef(null);
  // Early-phase per-peer diagnostics (timers/attempt counts per remote peer)
  const peerDiagnosticsRef = useRef(new Map()); // remoteId -> { timer, startedAt, iceFailures, attempts, firstRemoteMediaAt, lastState }

  const iceServers = useMemo(() => {
    try {
      const username = process.env.REACT_APP_TURN_USERNAME;
      const credential = process.env.REACT_APP_TURN_CREDENTIAL;
      const turnUrls = [
        'turn:meet.edumee.vn:3478?transport=udp',
        'turn:meet.edumee.vn:3478?transport=tcp',
        'turns:meet.edumee.vn:5349?transport=tcp'
      ];
      const turn = (username && credential) ? [{ urls: turnUrls, username, credential }] : [];
      return { iceServers: [...DEFAULT_STUN, ...turn] };
    } catch {
      return { iceServers: DEFAULT_STUN };
    }
  }, []);

  const addNotification = useCallback((payload) => {
    const pushOne = (obj) => {
      const id = Date.now() + Math.random();
      setNotifications(prev => [...prev, { id, ...obj }]);
    };
    if (typeof payload === 'string') {
      const lines = String(payload).split(/\r?\n/).filter(l => l.trim().length > 0);
      if (lines.length <= 1) {
        pushOne({ msg: payload });
      } else {
        lines.forEach(line => pushOne({ msg: line }));
      }
    } else if (payload && typeof payload === 'object') {
      const base = { ...payload };
      const raw = (base.msg || base.fallback || '');
      const lines = typeof raw === 'string' ? raw.split(/\r?\n/) : [];
      if (lines.length > 1) {
        lines.filter(l => l.trim().length > 0).forEach(line => pushOne({ ...base, msg: line, fallback: line }));
      } else {
        pushOne(base);
      }
    }
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Helper: get current local mic track (if any)
  const getLocalMicTrack = useCallback(() => {
    try {
      return localStreamRef.current?.getAudioTracks?.()[0] || null;
    } catch { return null; }
  }, []);

  // Ensure every PC has a sendrecv audio transceiver and a live mic track attached to its sender.
  // Also ensures sender encodings are marked active.
  // Returns a promise that resolves when all track replacements are complete.
  const ensureAudioSendersActive = useCallback(async (providedMicTrack) => {
    try {
      const micTrack = providedMicTrack || getLocalMicTrack();
      // Validate track is live before attempting replacement
      if (micTrack && micTrack.readyState !== 'live') {
        console.warn('[RTC] ensureAudioSendersActive: mic track is not live, readyState=', micTrack.readyState);
        return;
      }
      const replacePromises = [];
      peersRef.current.forEach((pc, remoteId) => {
        try {
          // Skip if peer connection is not in a usable state
          const connState = pc.connectionState || '';
          const iceState = pc.iceConnectionState || '';
          if (connState === 'closed' || connState === 'failed' || iceState === 'closed') {
            return;
          }
          let tx = null;
          const txs = typeof pc.getTransceivers === 'function' ? pc.getTransceivers() : [];
          // Prefer an existing audio transceiver if present
          tx = txs.find(t => {
            try {
              const kind = t?.receiver?.track?.kind || t?.sender?.track?.kind || '';
              return kind === 'audio';
            } catch { return false; }
          }) || null;
          // If none, create one so we always have an m-line to send on
          if (!tx) {
            try { tx = pc.addTransceiver('audio', { direction: 'sendrecv' }); } catch {}
          }
          if (tx) {
            try { typeof tx.setDirection === 'function' ? tx.setDirection('sendrecv') : (tx.direction = 'sendrecv'); } catch {}
            const sender = tx.sender || (typeof pc.getSenders === 'function' ? pc.getSenders().find(s => s && (s.track?.kind === 'audio')) : null);
            if (sender && micTrack) {
              // Await replaceTrack to avoid race conditions
              const replaceP = sender.replaceTrack(micTrack).then(() => {
                // Ensure any encodings are active
                try {
                  const params = sender.getParameters?.() || {};
                  if (Array.isArray(params.encodings)) {
                    let changed = false;
                    params.encodings.forEach(enc => { if (enc && enc.active === false) { enc.active = true; changed = true; } });
                    if (changed) { return sender.setParameters(params).catch(()=>{}); }
                  }
                } catch {}
              }).catch(e => {
                console.warn('[RTC] replaceTrack failed for peer', remoteId, e?.message || e);
              });
              replacePromises.push(replaceP);
            } else if (!sender && micTrack) {
              // Fallback: addTrack in case sender not exposed
              try { pc.addTrack(micTrack, localStreamRef.current); } catch {}
            }
          }
        } catch {}
      });
      // Wait for all track replacements to complete
      if (replacePromises.length > 0) {
        await Promise.allSettled(replacePromises);
      }
    } catch {}
  }, [getLocalMicTrack]);

  // Initialize socket connection
  useEffect(() => {
    if (!roomId) return;
    const token = getAuthToken();

    // Resolve RTC Socket.IO base URL specifically for /rtc namespace
    // Priority:
    // 1) REACT_APP_RTC_SOCKET_URL
    // 2) REACT_APP_API_BASE_URL (strip trailing /api)
    // 3) window.location.origin
    // 4) fallback '/'
    const resolveRtcBase = () => {
      const rtcEnv = process.env.REACT_APP_RTC_SOCKET_URL;
      if (rtcEnv) return rtcEnv.replace(/\/$/, '');
      const apiBase = process.env.REACT_APP_API_BASE_URL;
      if (apiBase) return String(apiBase).replace(/\/+$/, '').replace(/\/?api$/, '');
      if (typeof window !== 'undefined') {
        try {
          const { protocol, hostname, port } = window.location || {};
          return `${protocol}//${hostname}${port ? (':' + port) : ''}`;
        } catch {}
      }
      return '';
    };

    const base = resolveRtcBase();
    const namespace = '/rtc';
    const url = (base || '').replace(/\/$/, '') + namespace;

    const transportEnv = (process.env.REACT_APP_RTC_TRANSPORTS || '').split(',').map(s => s.trim()).filter(Boolean);
    const resolvedTransports = transportEnv.length > 0
        ? transportEnv
        : ['websocket', 'polling']; // default to both for resilience

    const s = io(url, {
      auth: { token, displayName, groupName },
      transports: resolvedTransports,
      withCredentials: true,
      path: process.env.REACT_APP_RTC_SOCKET_PATH || '/socket.io',
      timeout: 45000,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
      forceNew: true,
      query: { _joinNonce: String(Date.now() + Math.random()) }
    });
    socketRef.current = s;

    s.on('connect_error', (err) => {
      const msg = err?.message || String(err || 'unknown');
      addNotification({ fallback: `Signaling error: ${msg} (url=${url})`, severity: 'error' });
    });

    // On (re)connect, if we previously joined, attempt to rejoin seamlessly
    s.on('connect', () => {
      const payload = lastJoinParamsRef.current;
      if (payload && payload.roomId) {
        try { s.emit('join', payload); } catch {}
      }
    });

    s.on('joined', ({ id, hostId: hId, groupName: g }) => {
      if (g) setRoomGroupName(g);
      setSelfId(id);
      setHostId(hId);
      setEndedByHost(false);
      setJoined(true);
      s.emit('request-participants');
      // OPTIONAL: auto-start local media immediately after join (may require user gesture on some browsers)
      const AUTO_START_LOCAL_MEDIA = false;
      if (AUTO_START_LOCAL_MEDIA) { try { startLocalMedia().catch(()=>{}); } catch {} }
    });

    s.on('room-info', ({ groupName: g }) => {
      if (g) setRoomGroupName(g);
    });

    s.on('participants', ({ participants: list, hostId: hId, screenSharerId: ssid, groupName: g }) => {
      setHostId(hId);
      setScreenSharerId(ssid || null);
      if (g) setRoomGroupName(g);
      // Initialize peer connections for participants and proactively make offers to establish connections
      list.filter(p => p.id !== s.id).forEach(async (p) => {
        if (!peersRef.current.has(p.id)) {
          await createPeerConnection(p.id);
        }
        // Important: create offers to existing participants to ensure media flows
        try { setTimeout(() => { try { makeOffer(p.id).catch(()=>{}); } catch {} }, 0); } catch {}
      });
      // Ensure participants state has placeholders so UI can render tiles even before streams arrive
      setParticipants(prev => {
        const copy = { ...prev };
        // preserve self entry if exists
        if (copy.self) {
          copy.self = { ...copy.self };
        }
        list.forEach(p => {
          if (p.id === s.id) return; // skip self (handled via local media)
          const existing = copy[p.id];
          copy[p.id] = {
            id: p.id,
            name: p.name || existing?.name || '',
            stream: existing?.stream || null
          };
        });
        // Remove peers that no longer exist from participants map as well
        const existingIds = new Set(list.map(p => p.id));
        Object.keys(copy).forEach(key => {
          if (key !== 'self' && !existingIds.has(key)) {
            delete copy[key];
          }
        });
        return copy;
      });
      // Remove peers that no longer exist
      const existingIds = new Set(list.map(p => p.id));
      for (const id of Array.from(peersRef.current.keys())) {
        if (!existingIds.has(id)) {
          closePeer(id);
        }
      }
    });

    s.on('participant-joined', async ({ id, name }) => {
      addNotification({ i18nKey: 'rtc.participantJoined', params: { name: name || 'Someone' }, fallback: `${name || 'Someone'} joined`, color: 'green', severity: 'success' });
      // Add placeholder participant so UI shows a tile immediately
      setParticipants(prev => ({ ...prev, [id]: { id, name: name || '', stream: prev[id]?.stream || null } }));
      await createPeerConnection(id);
      // As caller, create offer
      setTimeout(() => { try { makeOffer(id).catch(()=>{}); } catch {} }, 0);
    });

    s.on('participant-left', ({ id }) => {
      closePeer(id);
    });

    s.on('offer', async ({ from, description }) => {
      const pc = await createPeerConnection(from);
      const meta = getMeta(from);
      // Decide polite side deterministically
      if (typeof meta.polite !== 'boolean') decidePolite(from);
      const readyForOffer = pc.signalingState === 'stable' || pc.signalingState === 'have-local-offer';
      const offerCollision = meta.makingOffer || !readyForOffer;
      try {
        if (offerCollision) {
          if (!meta.polite) {
            // Ignore the offer; non-polite peer sticks with its local offer
            return;
          }
          // Rollback our local description to apply the remote offer
          try { await pc.setLocalDescription({ type: 'rollback' }); } catch {}
        }
        await pc.setRemoteDescription(normalizeDescription(description));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        s.emit('answer', { to: from, description: pc.localDescription });
      } catch (e) {
        try {
          const msg = (e && (e.message || e.name || '')) + '';
          const lowered = msg.toLowerCase();
          if (lowered.includes('wrong state') || lowered.includes('have-remote-offer') || lowered.includes('have-local-offer')) {
            console.warn('[RTC] Benign negotiation race or state conflict during setup:', msg);
          }
        } catch {}
        try { console.warn('[RTC] error handling offer from', from, e && (e.message || e)); } catch {}
      }
    });

    s.on('answer', async ({ from, description }) => {
      const pc = peersRef.current.get(from);
      if (!pc) return;
      try {
        // Only apply remote answer if we still have a pending local offer.
        if (pc.signalingState !== 'have-local-offer') {
          return; // ignore late/stray answer
        }
        await pc.setRemoteDescription(normalizeDescription(description));
      } catch (e) {
        // Race-safety: if the state changed between the check and setRemoteDescription,
        // just ignore this answer silently (glare resolution or new negotiation underway).
        try {
          const stateNow = pc.signalingState;
          const connNow = pc.connectionState;
          const msg = (e && (e.message || e.name || '')) + '';
          const lowered = msg.toLowerCase();
          const isSslRole = lowered.includes('ssl role') || lowered.includes('dtls');
          const benign = stateNow !== 'have-local-offer' || lowered.includes('wrong state') || lowered.includes('have-remote-offer') || lowered.includes('have-local-offer') || isSslRole;
          if (benign) {
            // If this looks like a DTLS/SSL role race and we aren't connected yet, try a gentle ICE restart to recover.
            try {
              if (isSslRole && connNow !== 'connected' && connNow !== 'completed') {
                setTimeout(() => { try { makeOfferRef.current && makeOfferRef.current(from, { iceRestart: true }).catch(()=>{}); } catch {} }, 100);
              }
            } catch {}
            return; // suppress warning for benign races
          }
        } catch {}
        try { console.warn('[RTC] error handling answer from', from, e && (e.message || e)); } catch {}
      }
    });

    s.on('ice-candidate', async ({ from, candidate }) => {
      const pc = peersRef.current.get(from);
      if (pc && candidate) {
        try { await pc.addIceCandidate(candidate); } catch {}
      }
    });

    s.on('screen-share', ({ id, active }) => {
      setScreenSharerId(active ? id : null);
    });

    s.on('chat', (payload) => {
      setChatMessages(prev => [...prev, payload]);
    });

    s.on('end-call', () => {
      addNotification({ i18nKey: 'rtc.callEndedByHost', fallback: 'Call ended by host', severity: 'warning' });
      setEndedByHost(true);
      try { cleanup(); } catch {}
      // Force a page reload to reset any UI state after host ends the call
      try { setTimeout(() => { try { window.location.reload(); } catch (_) {} }, 150); } catch {}
    });

    s.connect();

    return () => {
      try { s.disconnect(); } catch {}
      cleanup();
    };
  }, [roomId, displayName, groupName, addNotification]);

  // Device preferences and enumeration
  const [devices, setDevices] = useState({ audioInputs: [], videoInputs: [], audioOutputs: [], defaultAudioInputLabel: '', defaultAudioOutputLabel: '' });
  const [selectedDevices, setSelectedDevices] = useState({ audioInputId: null, videoInputId: null, audioOutputId: null });

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;
    try {
      const list = await navigator.mediaDevices.enumerateDevices();
      let audioInputs = list.filter(d => d.kind === 'audioinput');
      const videoInputs = list.filter(d => d.kind === 'videoinput');
      let audioOutputs = list.filter(d => d.kind === 'audiooutput');
      // Remove Vietnamese-labeled default/output entries starting with "Loa" (e.g., "Loa mặc định ...")
      try { audioOutputs = audioOutputs.filter(d => !/^\s*Loa\b/i.test(String(d.label || ''))); } catch {}
      // Keep raw copies for cross-checking labels before we mutate lists
      const rawAudioInputs = audioInputs.slice();
      const rawAudioOutputs = audioOutputs.slice();
      // Normalize labels by stripping any leading "Default - ".
      const norm = (s) => String(s || '').toLowerCase().replace(/^default\s*-\s*/, '').trim();
      const stripDefaultPrefix = (s) => String(s || '').replace(/^Default\s*-\s*/i, '').trim();
      let defaultAudioInputLabel = '';
      let defaultAudioOutputLabel = '';
      // De-duplicate inputs: keep 'default' when it maps to same physical device label.
      try {
        const defIn = audioInputs.find(d => d.deviceId === 'default');
        if (defIn && defIn.label) {
          const defInLabel = norm(defIn.label);
          const inMatch = rawAudioInputs.find(d => d.deviceId !== 'default' && norm(d.label) === defInLabel);
          defaultAudioInputLabel = inMatch ? stripDefaultPrefix(inMatch.label) : '';
          audioInputs = audioInputs.filter(d => d.deviceId === 'default' || norm(d.label) !== defInLabel);
        }
      } catch {}
      // De-duplicate outputs similarly.
      try {
        const defOut = audioOutputs.find(d => d.deviceId === 'default');
        if (defOut && defOut.label) {
          const defOutLabel = norm(defOut.label);
          const outMatch = rawAudioOutputs.find(d => d.deviceId !== 'default' && norm(d.label) === defOutLabel);
          defaultAudioOutputLabel = outMatch ? stripDefaultPrefix(outMatch.label) : '';
          audioOutputs = audioOutputs.filter(d => d.deviceId === 'default' || norm(d.label) !== defOutLabel);
        }
      } catch {}
      setDevices({ audioInputs, videoInputs, audioOutputs, defaultAudioInputLabel, defaultAudioOutputLabel });
      // Try to keep selected ids valid
      setSelectedDevices(prev => ({
        audioInputId: prev.audioInputId && audioInputs.some(d => d.deviceId === prev.audioInputId) ? prev.audioInputId : (audioInputs[0]?.deviceId || null),
        videoInputId: prev.videoInputId && videoInputs.some(d => d.deviceId === prev.videoInputId) ? prev.videoInputId : (videoInputs[0]?.deviceId || null),
        audioOutputId: prev.audioOutputId && audioOutputs.some(d => d.deviceId === prev.audioOutputId) ? prev.audioOutputId : (audioOutputs[0]?.deviceId || null),
      }));
    } catch (e) {
      try { console.warn('[RTC] enumerateDevices failed', e && (e.name || e.message || e)); } catch {}
    }
  }, []);

  useEffect(() => {
    // Load saved selections
    try {
      const saved = JSON.parse(localStorage.getItem('rtc.devicePrefs') || 'null');
      if (saved) setSelectedDevices(prev => ({ ...prev, ...saved }));
    } catch {}
    refreshDevices();
    const handler = () => refreshDevices();
    try { navigator.mediaDevices?.addEventListener?.('devicechange', handler); } catch {}
    return () => { try { navigator.mediaDevices?.removeEventListener?.('devicechange', handler); } catch {} };
  }, [refreshDevices]);

  useEffect(() => {
    try { localStorage.setItem('rtc.devicePrefs', JSON.stringify(selectedDevices)); } catch {}
  }, [selectedDevices]);

  const startLocalMedia = useCallback(async () => {
    // Try to unlock iOS/Safari audio policies before requesting devices
    try { await unlockIOSAudio(); } catch {}
    if (localStreamRef.current) return localStreamRef.current;
    // Warn if not using a secure context (required by browsers for mic access except localhost)
    try {
      if (typeof window !== 'undefined' && window.isSecureContext === false && !/^localhost(:\d+)?$/.test(window.location.hostname)) {
        addNotification({ i18nKey: 'rtc.insecureContext', fallback: 'Microphone requires HTTPS. Please use a secure connection.', severity: 'warning' });
      }
    } catch {}

    const audioConstraintSelected = selectedDevices.audioInputId
        ? { deviceId: { exact: selectedDevices.audioInputId }, channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
        : { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true }; // Prefer mono for better device compatibility
    const videoConstraint = selectedDevices.videoInputId ? { deviceId: { exact: selectedDevices.videoInputId } } : true;

    let stream;
    // Try to get both audio and video first via safe wrapper
    try {
      ({ stream } = await getUserMediaSafe({ video: videoConstraint, audio: audioConstraintSelected }));
    } catch (err) {
      const name = err && err.name;
      const msg = (err && (err.message || '')) || '';
      const lowered = (name ? name : msg).toLowerCase();
      // If specific mic requested but not available, reset selection and retry with generic mic
      if (selectedDevices.audioInputId && (name === 'NotFoundError' || name === 'OverconstrainedError')) {
        try {
          ({ stream } = await getUserMediaSafe({ video: videoConstraint, audio: true }));
          setSelectedDevices(prev => ({ ...prev, audioInputId: null }));
        } catch {}
      }
      // If still no stream (e.g., camera missing or blocked), fall back to audio-only, then video-only
      if (!stream) {
        try {
          ({ stream } = await getUserMediaSafe({ audio: audioConstraintSelected, video: false }));
        } catch {}
      }
      if (!stream) {
        try {
          ({ stream } = await getUserMediaSafe({ video: videoConstraint, audio: false }));
        } catch {}
      }
      if (!stream) {
        if (
            name === 'NotAllowedError' ||
            lowered.includes('denied') ||
            lowered.includes('permission')
        ) {
          addNotification({ i18nKey: 'rtc.signalStateConflict', fallback: 'Connection hiccup during setup. If audio/video is not working, please allow permissions and reload the page.', severity: 'error', sticky: true });
        } else if (name === 'NotFoundError' || lowered.includes('no device')) {
          addNotification({ i18nKey: 'rtc.noMediaFound', fallback: 'No camera or microphone found. Please connect a device and reload the page.', severity: 'error', sticky: true });
        } else if (name === 'NotReadableError' || lowered.includes('in use')) {
          addNotification({ i18nKey: 'rtc.mediaInUse', fallback: 'Camera/Microphone is in use by another application. Close other apps and try again, then reload the page.', severity: 'error', sticky: true });
        } else if (name === 'SecurityError' || lowered.includes('insecure') || lowered.includes('https')) {
          addNotification({ i18nKey: 'rtc.insecureContext', fallback: 'Camera/Microphone requires HTTPS. Please use a secure connection.', severity: 'warning' });
        } else {
          addNotification({ i18nKey: 'rtc.mediaError', fallback: 'Could not start camera/microphone. Please check permissions and reload the page.', severity: 'error' });
        }
        throw err;
      }
    }

    try {
      // Apply desired toggle states in case user toggled before permissions
      try { stream.getAudioTracks().forEach(t => (t.enabled = !!desiredMicEnabledRef.current)); } catch {}
      try { stream.getVideoTracks().forEach(t => (t.enabled = !!desiredCamEnabledRef.current)); } catch {}
      localStreamRef.current = stream;
      // Warn if no audio or video tracks available
      try {
        const hasAudio = (stream.getAudioTracks?.() || []).length > 0;
        const hasVideo = (stream.getVideoTracks?.() || []).length > 0;
        if (!hasAudio) addNotification({ i18nKey: 'rtc.noMicFound', fallback: 'No microphone captured. Check permissions or reload the page to retry.', severity: 'error', sticky: true });
        if (!hasVideo) addNotification({ i18nKey: 'rtc.noCameraFound', fallback: 'No camera captured. Check permissions or reload the page to retry.', severity: 'warning' });
      } catch {}
      // Update simple state flags based on actual track enabled states
      try { setMicEnabled(stream.getAudioTracks().some(t => t.enabled !== false)); } catch { setMicEnabled(true); }
      try { setCameraEnabled(stream.getVideoTracks().some(t => t.enabled !== false)); } catch { setCameraEnabled(true); }
      // Refresh devices so labels become available after permissions are granted
      try { await refreshDevices(); } catch {}
      // Watch for mic track ending (device unplugged or permission revoked)
      try {
        stream.getAudioTracks().forEach(t => {
          try { t.addEventListener('ended', () => { addNotification({ i18nKey: 'rtc.micEnded', fallback: 'Microphone disconnected or stopped. Please select another device.', severity: 'warning' }); refreshDevices(); }); } catch {}
          try { t.addEventListener('mute', () => { addNotification({ i18nKey: 'rtc.micMutedBySystem', fallback: 'Microphone was muted by the system.', severity: 'info' }); }); } catch {}
          try { t.addEventListener('unmute', () => { addNotification({ i18nKey: 'rtc.micUnmuted', fallback: 'Microphone is active again.', severity: 'success' }); }); } catch {}
        });
      } catch {}
      // Attach to all existing peers (promote existing recvonly transceivers to sendrecv) and ensure a sender is wired
      const micTrack = stream.getAudioTracks?.()[0] || null;
      try { await ensureAudioSendersActive(micTrack); } catch {}
      // Video track: replace or add where needed - properly await replacements
      try {
        const vTracks = stream.getVideoTracks?.() || [];
        const vTrack = vTracks[0] || null;
        if (vTrack && vTrack.readyState === 'live') {
          const videoReplacePromises = [];
          for (const [remoteId, pc] of peersRef.current.entries()) {
            try {
              const connState = pc.connectionState || '';
              if (connState === 'closed' || connState === 'failed') continue;
              const txs = typeof pc.getTransceivers === 'function' ? pc.getTransceivers() : [];
              const videoTx = txs.find(tx => tx?.receiver?.track?.kind === 'video') ||
                  txs.find(tx => tx?.sender?.track?.kind === 'video');
              if (videoTx && videoTx.sender) {
                try { typeof videoTx.setDirection === 'function' ? videoTx.setDirection('sendrecv') : (videoTx.direction = 'sendrecv'); } catch {}
                videoReplacePromises.push(
                  videoTx.sender.replaceTrack(vTrack).catch(e => {
                    console.warn('[RTC] startLocalMedia video replaceTrack failed for peer', remoteId, e?.message || e);
                  })
                );
              } else {
                try { pc.addTransceiver('video', { direction: 'sendrecv' }); } catch {}
                try { pc.addTrack(vTrack, stream); } catch {}
              }
            } catch {}
          }
          if (videoReplacePromises.length > 0) {
            await Promise.allSettled(videoReplacePromises);
          }
        }
      } catch {}
      // Proactively renegotiate so others can receive our newly added tracks
      try {
        for (const remoteId of peersRef.current.keys()) {
          try { setTimeout(() => { try { makeOffer(remoteId).catch(()=>{}); } catch {} }, 0); } catch {}
        }
      } catch {}
      setParticipants(prev => ({ ...prev, self: { id: 'self', name: displayName || 'Me', stream, isSelf: true } }));
      // Apply background effect if enabled and we have a video track
      try { if ((stream.getVideoTracks?.() || []).length > 0 && backgroundEffect !== 'none') { await applyBackgroundEffect(backgroundEffect); } } catch {}
      return stream;
    } catch (e) {
      const name = e && e.name;
      const msg = (e && (e.message || '')) || '';
      const lowered = (name ? name : msg).toLowerCase();
      if (
          name === 'NotAllowedError' ||
          lowered.includes('denied') ||
          lowered.includes('permission')
      ) {
        addNotification({ i18nKey: 'rtc.signalStateConflict', fallback: 'Connection hiccup during setup. If audio/video is not working, please allow permissions and reload the page.', severity: 'error', sticky: true });
      } else if (name === 'NotFoundError' || lowered.includes('no device')) {
        addNotification({ i18nKey: 'rtc.noMediaFound', fallback: 'No camera or microphone found. Please connect a device and reload the page.', severity: 'error', sticky: true });
      } else if (name === 'NotReadableError' || lowered.includes('in use')) {
        addNotification({ i18nKey: 'rtc.mediaInUse', fallback: 'Camera/Microphone is in use by another application. Close other apps and try again, then reload the page.', severity: 'error', sticky: true });
      } else if (name === 'SecurityError' || lowered.includes('insecure') || lowered.includes('https')) {
        addNotification({ i18nKey: 'rtc.insecureContext', fallback: 'Camera/Microphone requires HTTPS. Please use a secure connection.', severity: 'warning' });
      } else {
        addNotification({ i18nKey: 'rtc.mediaError', fallback: 'Could not start camera/microphone. Please check permissions and reload the page.', severity: 'error' });
      }
      throw e;
    }
  }, [displayName, selectedDevices.audioInputId, selectedDevices.videoInputId, addNotification, refreshDevices, backgroundEffect, ensureAudioSendersActive]);

  const setAudioInput = useCallback(async (deviceId) => {
    if (!deviceId) return false;
    // Ensure we have some local stream to attach to
    if (!localStreamRef.current) {
      try { await startLocalMedia(); } catch (e) { return false; }
    }
    const stream = localStreamRef.current;
    const prevTrack = stream?.getAudioTracks?.()[0] || null;
    const wasEnabled = prevTrack ? prevTrack.enabled : true;
    let newStream;
    try {
      ({ stream: newStream } = await getUserMediaSafe({ audio: { deviceId: { exact: deviceId }, channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false }));
    } catch (e) {
      const name = e && e.name;
      const msg = (e && (e.message || '')) || '';
      const lowered = (name ? name : msg).toLowerCase();
      if (
          name === 'NotAllowedError' ||
          lowered.includes('denied') ||
          lowered.includes('permission')
      ) {
        addNotification({ i18nKey: 'rtc.signalStateConflict', fallback: 'Connection hiccup during setup. If audio/video is not working, please allow permissions and reload the page.', severity: 'error', sticky: true });
      } else if (name === 'NotFoundError' || lowered.includes('no device')) {
        addNotification({ i18nKey: 'rtc.noMicFound', fallback: 'No microphone found. Connect a mic and reload the page.', severity: 'error', sticky: true });
      } else if (name === 'NotReadableError' || lowered.includes('in use')) {
        addNotification({ i18nKey: 'rtc.micInUse', fallback: 'Microphone is currently in use by another application. Close other apps and try again, then reload the page.', severity: 'error', sticky: true });
      } else {
        addNotification({ i18nKey: 'rtc.micError', fallback: 'Could not switch microphone. Check permissions and reload the page.', severity: 'error' });
      }
      return false;
    }
    const newTrack = newStream.getAudioTracks()[0];
    if (!newTrack) return false;
    // Notify if mic ends/mutes
    try { newTrack.addEventListener('ended', () => { addNotification({ i18nKey: 'rtc.micEnded', fallback: 'Microphone disconnected or stopped. Please select another device.', severity: 'warning' }); refreshDevices(); }); } catch {}
    try { newTrack.addEventListener('mute', () => { addNotification({ i18nKey: 'rtc.micMutedBySystem', fallback: 'Microphone was muted by the system.', severity: 'info' }); }); } catch {}
    try { newTrack.addEventListener('unmute', () => { addNotification({ i18nKey: 'rtc.micUnmuted', fallback: 'Microphone is active again.', severity: 'success' }); }); } catch {}
    // Apply previous enabled state
    try { newTrack.enabled = wasEnabled; } catch {}
    // Replace in local stream
    if (prevTrack) {
      try { prevTrack.stop(); } catch {}
      try { stream.removeTrack(prevTrack); } catch {}
    }
    try { stream.addTrack(newTrack); } catch {}
    // Ensure every peer has a sendrecv audio transceiver carrying this new track
    try { await ensureAudioSendersActive(newTrack); } catch {}
    // Proactively renegotiate
    try {
      for (const remoteId of peersRef.current.keys()) {
        try { setTimeout(() => { try { makeOfferRef.current && makeOfferRef.current(remoteId).catch(()=>{}); } catch {} }, 0); } catch {}
      }
    } catch {}
    setSelectedDevices(prev => ({ ...prev, audioInputId: deviceId }));
    // Update participants self stream reference to trigger re-render
    setParticipants(prev => ({ ...prev, self: { ...(prev.self || { id: 'self', name: displayName || 'Me' }), id: 'self', name: displayName || 'Me', stream, isSelf: true } }));
    // Notify user
    try {
      const d = (devices.audioInputs || []).find(d => d.deviceId === deviceId);
      const label = d?.label || 'Microphone';
      addNotification({ i18nKey: 'rtc.micSwitched', params: { label }, fallback: `Microphone switched to ${label}`, severity: 'success' });
    } catch {}
    return true;
  }, [startLocalMedia, displayName, devices, addNotification, refreshDevices, ensureAudioSendersActive]);

  const toggleMic = useCallback(async (on) => {
    desiredMicEnabledRef.current = !!on;
    const stream = localStreamRef.current;

    if (on) {
      // Check if we have a live audio track
      const existingTrack = stream?.getAudioTracks?.()?.[0];
      const trackIsLive = existingTrack && existingTrack.readyState === 'live';

      if (!trackIsLive) {
        // Track is not live, need to re-acquire microphone
        console.log('[RTC] toggleMic: track not live, re-acquiring microphone...');
        try {
          const audioConstraint = selectedDevices.audioInputId
            ? { deviceId: { exact: selectedDevices.audioInputId }, channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
            : { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true };
          const { stream: newStream } = await getUserMediaSafe({ audio: audioConstraint, video: false });
          const newTrack = newStream?.getAudioTracks?.()?.[0];

          if (newTrack) {
            // Remove old track if exists
            if (existingTrack && stream) {
              try { existingTrack.stop(); } catch {}
              try { stream.removeTrack(existingTrack); } catch {}
            }
            // Add new track to local stream
            if (stream) {
              try { stream.addTrack(newTrack); } catch {}
            } else {
              // Create new stream if none exists
              localStreamRef.current = newStream;
            }
            // Setup track event listeners
            try {
              newTrack.addEventListener('ended', () => {
                addNotification({ i18nKey: 'rtc.micEnded', fallback: 'Microphone disconnected or stopped.', severity: 'warning' });
                refreshDevices();
              });
            } catch {}
            // Ensure senders have the new track
            try { await ensureAudioSendersActive(newTrack); } catch {}
            // Update participants
            setParticipants(prev => ({
              ...prev,
              self: { ...(prev.self || { id: 'self', name: displayName || 'Me' }), stream: localStreamRef.current, isSelf: true }
            }));
            console.log('[RTC] toggleMic: microphone re-acquired successfully');
          }
        } catch (e) {
          console.error('[RTC] toggleMic: failed to re-acquire microphone', e?.message || e);
          addNotification({
            i18nKey: 'rtc.micReacquireFailed',
            fallback: 'Could not re-enable microphone. Please check permissions and try again.',
            severity: 'error'
          });
          setMicEnabled(false);
          throw new Error('Failed to re-acquire microphone');
        }
      } else {
        // Track is live, just enable it
        try { existingTrack.enabled = true; } catch {}
      }

      // Update participants to trigger UI re-render
      setParticipants(prev => ({
        ...prev,
        self: { ...(prev.self || { id: 'self', name: displayName || 'Me' }), stream: localStreamRef.current, isSelf: true }
      }));

      // Make sure a sender exists and is in sendrecv, then renegotiate
      try { await ensureAudioSendersActive(); } catch {}
      try {
        for (const remoteId of peersRef.current.keys()) {
          try { setTimeout(() => { try { makeOfferRef.current && makeOfferRef.current(remoteId).catch(()=>{}); } catch {} }, 50); } catch {}
        }
      } catch {}
    } else {
      // Turning off - just disable the track
      if (stream) {
        try { stream.getAudioTracks().forEach(t => (t.enabled = false)); } catch {}
      }
      // Update participants to trigger UI re-render
      setParticipants(prev => ({
        ...prev,
        self: { ...(prev.self || { id: 'self', name: displayName || 'Me' }), stream: localStreamRef.current, isSelf: true }
      }));
    }

    try { setMicEnabled(!!on); } catch {}
    return !!on;
  }, [ensureAudioSendersActive, selectedDevices.audioInputId, addNotification, refreshDevices, displayName]);

  // Utility: get an audio-only MediaStream for a given mic deviceId (or default if none)
  const getMicStream = useCallback(async (deviceId) => {
    try {
      const constraints = {
        audio: deviceId
            ? { deviceId: { exact: deviceId }, channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
            : { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false,
      };
      const { stream } = await getUserMediaSafe(constraints);
      const track = stream.getAudioTracks()?.[0] || null;
      try {
        console.log('[RTC] Mic track ready.', track ? { enabled: track.enabled, muted: track.muted, label: track.label } : { none: true });
      } catch {}
      if (track) {
        try { track.onended = () => { try { console.warn('[RTC] Mic track ended (permission revoked or device removed).'); } catch {} ; addNotification({ i18nKey: 'rtc.micEnded', fallback: 'Microphone disconnected or stopped. Please select another device.', severity: 'warning' }); refreshDevices(); }; } catch {}
        try { track.onmute = () => { try { console.warn('[RTC] Mic track muted by system.'); } catch {} ; addNotification({ i18nKey: 'rtc.micMutedBySystem', fallback: 'Microphone was muted by the system.', severity: 'info' }); }; } catch {}
        try { track.onunmute = () => { try { console.warn('[RTC] Mic track unmuted.'); } catch {} ; addNotification({ i18nKey: 'rtc.micUnmuted', fallback: 'Microphone is active again.', severity: 'success' }); }; } catch {}
      }
      return stream;
    } catch (e) {
      try { console.error('[RTC] getUserMedia (mic) failed:', e && (e.name || e.message || e)); } catch {}
      const name = e && e.name;
      const msg = (e && (e.message || '')) || '';
      const lowered = (name ? name : msg).toLowerCase();
      if (
          name === 'NotAllowedError' ||
          lowered.includes('denied') ||
          lowered.includes('permission')
      ) {
        addNotification({ i18nKey: 'rtc.signalStateConflict', fallback: 'Connection hiccup during setup. If audio/video is not working, please allow permissions and reload the page.', severity: 'error', sticky: true });
      } else if (name === 'NotFoundError' || lowered.includes('no device')) {
        addNotification({ i18nKey: 'rtc.noMicFound', fallback: 'No microphone found. Connect a mic and reload the page.', severity: 'error', sticky: true });
      } else if (name === 'NotReadableError' || lowered.includes('in use')) {
        addNotification({ i18nKey: 'rtc.micInUse', fallback: 'Microphone is currently in use by another application. Close other apps and try again, then reload the page.', severity: 'error', sticky: true });
      } else if (name === 'SecurityError' || lowered.includes('insecure') || lowered.includes('https')) {
        addNotification({ i18nKey: 'rtc.insecureContext', fallback: 'Microphone requires HTTPS. Please use a secure connection.', severity: 'warning' });
      } else {
        addNotification({ i18nKey: 'rtc.micError', fallback: 'Could not start microphone. Check permissions and reload the page.', severity: 'error' });
      }
      throw e;
    }
  }, [addNotification, refreshDevices]);

  // Change speakers (audio output) device: apply to all media elements when supported
  const setAudioOutput = useCallback(async (deviceId) => {
    setSelectedDevices(prev => ({ ...prev, audioOutputId: deviceId || null }));
    let applied = 0;
    try {
      const elements = Array.from(document.querySelectorAll('audio,video'));
      for (const el of elements) {
        try {
          if (typeof el.setSinkId === 'function') {
            await el.setSinkId(deviceId || 'default');
            applied++;
          }
        } catch {}
      }
      // Apply to hidden remote sinks too
      try {
        for (const el of remoteSinksRef.current.values()) {
          try {
            if (typeof el.setSinkId === 'function') {
              await el.setSinkId(deviceId || 'default');
              applied++;
            }
          } catch {}
        }
      } catch {}
    } catch {}
    // Notify if unsupported
    if (applied === 0) {
      addNotification({ i18nKey: 'rtc.outputNotSupported', fallback: 'Changing speakers is not supported by this browser.', severity: 'info' });
    } else {
      try {
        const d = (devices.audioOutputs || []).find(d => d.deviceId === deviceId);
        const label = d?.label || 'Speakers';
        addNotification({ i18nKey: 'rtc.speakersSwitched', params: { label }, fallback: `Speakers switched to ${label}`, severity: 'success' });
      } catch {}
    }
    return true;
  }, [devices, addNotification]);

  // Auto-fallback when selected devices are removed
  useEffect(() => {
    const ai = devices?.audioInputs || [];
    const ao = devices?.audioOutputs || [];
    // Mic removed
    if (selectedDevices.audioInputId && !ai.some(d => d.deviceId === selectedDevices.audioInputId)) {
      if (ai[0]) {
        setAudioInput(ai[0].deviceId);
        addNotification({ i18nKey: 'rtc.micRemovedSwitched', fallback: 'Microphone removed. Switched to default.', severity: 'warning' });
      } else {
        addNotification({ i18nKey: 'rtc.noMicFound', fallback: 'No microphone found.', severity: 'error', sticky: true });
      }
    }
    // Speakers removed
    if (selectedDevices.audioOutputId && !ao.some(d => d.deviceId === selectedDevices.audioOutputId)) {
      const fallback = ao[0]?.deviceId || 'default';
      setAudioOutput(fallback);
      addNotification({ i18nKey: 'rtc.speakersRemovedSwitched', fallback: 'Speakers removed. Switched to default.', severity: 'warning' });
    }
  }, [devices, selectedDevices.audioInputId, selectedDevices.audioOutputId, setAudioInput, setAudioOutput, addNotification]);

  const toggleCamera = useCallback(async (on) => {
    desiredCamEnabledRef.current = !!on;
    const stream = localStreamRef.current;

    if (on) {
      // Check if we have a live video track
      const existingTrack = stream?.getVideoTracks?.()?.[0];
      const trackIsLive = existingTrack && existingTrack.readyState === 'live';

      if (!trackIsLive) {
        // Track is not live, need to re-acquire camera
        console.log('[RTC] toggleCamera: track not live, re-acquiring camera...');
        try {
          const videoConstraint = selectedDevices.videoInputId
            ? { deviceId: { exact: selectedDevices.videoInputId } }
            : true;
          const { stream: newStream } = await getUserMediaSafe({ audio: false, video: videoConstraint });
          const newTrack = newStream?.getVideoTracks?.()?.[0];

          if (newTrack) {
            // Remove old track if exists
            if (existingTrack && stream) {
              try { existingTrack.stop(); } catch {}
              try { stream.removeTrack(existingTrack); } catch {}
            }
            // Add new track to local stream
            if (stream) {
              try { stream.addTrack(newTrack); } catch {}
            } else {
              // Create new stream if none exists
              localStreamRef.current = newStream;
            }
            // Setup track event listeners
            try {
              newTrack.addEventListener('ended', () => {
                addNotification({ i18nKey: 'rtc.cameraEnded', fallback: 'Camera disconnected or stopped.', severity: 'warning' });
                refreshDevices();
              });
            } catch {}

            // Replace video track in all peer connections
            const replacePromises = [];
            peersRef.current.forEach((pc, remoteId) => {
              try {
                const connState = pc.connectionState || '';
                if (connState === 'closed' || connState === 'failed') return;
                const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
                if (sender) {
                  replacePromises.push(
                    sender.replaceTrack(newTrack).catch(e => {
                      console.warn('[RTC] toggleCamera replaceTrack failed for peer', remoteId, e?.message || e);
                    })
                  );
                } else {
                  // No video sender, add the track
                  try { pc.addTrack(newTrack, localStreamRef.current); } catch {}
                }
              } catch {}
            });
            if (replacePromises.length > 0) {
              await Promise.allSettled(replacePromises);
            }

            // Renegotiate with all peers
            try {
              for (const remoteId of peersRef.current.keys()) {
                try { setTimeout(() => { try { makeOfferRef.current && makeOfferRef.current(remoteId).catch(()=>{}); } catch {} }, 50); } catch {}
              }
            } catch {}

            // Update participants
            setParticipants(prev => ({
              ...prev,
              self: { ...(prev.self || { id: 'self', name: displayName || 'Me' }), stream: localStreamRef.current, isSelf: true }
            }));
            console.log('[RTC] toggleCamera: camera re-acquired successfully');
          }
        } catch (e) {
          console.error('[RTC] toggleCamera: failed to re-acquire camera', e?.message || e);
          addNotification({
            i18nKey: 'rtc.cameraReacquireFailed',
            fallback: 'Could not re-enable camera. Please check permissions and try again.',
            severity: 'error'
          });
          setCameraEnabled(false);
          throw new Error('Failed to re-acquire camera');
        }
      } else {
        // Track is live, just enable it
        try { existingTrack.enabled = true; } catch {}
        // Update participants to trigger UI re-render
        setParticipants(prev => ({
          ...prev,
          self: { ...(prev.self || { id: 'self', name: displayName || 'Me' }), stream: localStreamRef.current, isSelf: true }
        }));
      }
    } else {
      // Turning off - just disable the track
      if (stream) {
        try { stream.getVideoTracks().forEach(t => (t.enabled = false)); } catch {}
      }
      // Update participants to trigger UI re-render
      setParticipants(prev => ({
        ...prev,
        self: { ...(prev.self || { id: 'self', name: displayName || 'Me' }), stream: localStreamRef.current, isSelf: true }
      }));
    }

    try { setCameraEnabled(!!on); } catch {}
    return !!on;
  }, [selectedDevices.videoInputId, addNotification, refreshDevices, displayName]);

  const stopScreenShare = useCallback(async () => {
    const stream = screenStreamRef.current;
    if (!stream) return false;
    stream.getTracks().forEach(t => t.stop());
    screenStreamRef.current = null;
    // Restore camera video
    const camTrack = localStreamRef.current?.getVideoTracks()?.[0] || null;
    if (camTrack && camTrack.readyState === 'live') {
      // Properly await all track replacements
      const replacePromises = [];
      peersRef.current.forEach((pc, remoteId) => {
        try {
          // Skip closed connections
          const connState = pc.connectionState || '';
          if (connState === 'closed' || connState === 'failed') return;
          const senders = pc.getSenders().filter(s => s.track && s.track.kind === 'video');
          if (senders[0]) {
            replacePromises.push(
              senders[0].replaceTrack(camTrack).catch(e => {
                console.warn('[RTC] stopScreenShare replaceTrack failed for peer', remoteId, e?.message || e);
              })
            );
          }
        } catch {}
      });
      // Wait for all replacements before renegotiating
      if (replacePromises.length > 0) {
        await Promise.allSettled(replacePromises);
      }
      // Renegotiate so camera is restored for receivers
      try {
        for (const [remoteId] of peersRef.current.entries()) {
          try { setTimeout(() => { try { makeOfferRef.current && makeOfferRef.current(remoteId).catch(()=>{}); } catch {} }, 50); } catch {}
        }
      } catch {}
    }
    // Notify and clear local sharer id
    socketRef.current?.emit('screen-share', { id: selfId || null, active: false });
    try { setScreenSharerId(null); } catch {}
    try { setScreenSharing(false); } catch {}
    // Re-apply background effect if enabled
    try { if (backgroundEffect !== 'none') { await applyBackgroundEffect(backgroundEffect); } } catch {}
    return true;
  }, [backgroundEffect, selfId]);

  const startScreenShare = useCallback(async () => {
    if (screenStreamRef.current) return true;
    // Stop virtual background while screen sharing
    try { await stopVirtualBackground(); } catch {}
    let stream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
    } catch (e) {
      const name = e && e.name;
      const msg = (e && (e.message || '')) || '';
      const lowered = (name ? name : msg).toLowerCase();
      if (
          name === 'NotAllowedError' ||
          name === 'AbortError' ||
          lowered.includes('denied') ||
          lowered.includes('permission') ||
          lowered.includes('cancel')
      ) {
        addNotification({ i18nKey: 'rtc.screenSharePermissionDenied', fallback: 'Screen sharing was blocked or canceled. Please allow screen share permission and try again.', severity: 'error' });
        return false;
      }
      try { console.warn('[RTC] getDisplayMedia failed:', name || msg, e); } catch {}
      addNotification({ i18nKey: 'rtc.screenShareError', fallback: 'Could not start screen sharing. Please try again.', severity: 'error' });
      return false;
    }
    const vTrack = stream.getVideoTracks()[0];
    try { if (vTrack && 'contentHint' in vTrack) vTrack.contentHint = 'detail'; } catch {}
    screenStreamRef.current = stream;

    // For each peer, prefer replacing existing video sender; if none, add the screen track
    // Properly await all track replacements to avoid race conditions
    const replacePromises = [];
    peersRef.current.forEach((pc, remoteId) => {
      try {
        // Skip closed connections
        const connState = pc.connectionState || '';
        if (connState === 'closed' || connState === 'failed') return;
        const videoSenders = pc.getSenders().filter(s => s.track && s.track.kind === 'video');
        if (videoSenders.length > 0 && vTrack) {
          replacePromises.push(
            videoSenders[0].replaceTrack(vTrack).catch(e => {
              console.warn('[RTC] startScreenShare replaceTrack failed for peer', remoteId, e?.message || e);
            })
          );
        } else if (vTrack) {
          try { pc.addTrack(vTrack, stream); } catch {}
        }
      } catch {}
    });

    // Wait for all replacements before renegotiating
    if (replacePromises.length > 0) {
      await Promise.allSettled(replacePromises);
    }

    // Proactively renegotiate so new screen track is received
    try {
      for (const [remoteId] of peersRef.current.entries()) {
        try { setTimeout(() => { try { makeOfferRef.current && makeOfferRef.current(remoteId).catch(()=>{}); } catch {} }, 50); } catch {}
      }
    } catch {}

    // Notify others via signaling and set local state so UI shows it immediately
    try { setScreenSharerId(selfId || null); } catch {}
    socketRef.current?.emit('screen-share', { id: selfId || null, active: true });
    try { setScreenSharing(true); } catch {}

    // Auto stop when user stops sharing from browser/system UI
    try {
      const stopper = () => { try { stopScreenShare(); } catch {} };
      if (vTrack && typeof vTrack.addEventListener === 'function') {
        try { vTrack.addEventListener('ended', stopper); } catch {}
        try { vTrack.onended = stopper; } catch {}
      }
      const aTrack = (typeof stream.getAudioTracks === 'function') ? (stream.getAudioTracks()[0] || null) : null;
      if (aTrack && typeof aTrack.addEventListener === 'function') {
        try { aTrack.addEventListener('ended', stopper); } catch {}
        try { aTrack.onended = stopper; } catch {}
      }
      if (stream) {
        try { stream.addEventListener && stream.addEventListener('inactive', stopper); } catch {}
        try { stream.addEventListener && stream.addEventListener('ended', stopper); } catch {}
        try { stream.oninactive = stopper; } catch {}
        try { stream.onended = stopper; } catch {}
      }
    } catch {}
    return true;
  }, [stopVirtualBackground, addNotification, selfId, stopScreenShare]);

  const sendChat = useCallback((message) => {
    if (!message) return;
    socketRef.current?.emit('chat', { message, ts: Date.now() });
  }, []);

  const endCall = useCallback(() => {
    const isHost = selfId && hostId && String(selfId) === String(hostId);
    if (isHost) {
      // Host ends the call for everyone
      try { socketRef.current?.emit('end-call'); } catch {}
    } else {
      // Non-host just leaves the meeting
      try { socketRef.current?.emit('leave'); } catch {}
    }
    // In all cases, cleanup locally immediately so devices are released
    try { cleanup(); } catch {}
    // Force a page reload shortly after ending the call to ensure a clean state (if UX allows)
    try { setTimeout(() => { try { window.location.reload(); } catch (_) {} }, 150); } catch {}
  }, [selfId, hostId]);

  const leave = useCallback(() => {
    try { socketRef.current?.emit('leave'); } catch {}
    try { cleanup(); } catch {}
    // Force a page reload shortly after leaving the call to ensure a clean state (if UX allows)
    try { setTimeout(() => { try { window.location.reload(); } catch (_) {} }, 150); } catch {}
  }, []);

  // Diagnostics: check if we are sending audio (packets and audioLevel)

  const logOutgoingAudioLevel = useCallback(async () => {
    try {
      const local = localStreamRef.current;
      const at = local?.getAudioTracks?.()[0] || null;
      if (at) {
        console.log('[RTC] local mic track:', {
          enabled: !!at.enabled,
          readyState: at.readyState,
          muted: !!at.muted,
          label: at.label || '(unlabeled)'
        });
      } else {
        console.log('[RTC] local mic track: none');
      }
      const entries = Array.from(peersRef.current.entries());
      // Log this only sparingly to avoid noise and confusion; peers can legitimately be 0 before signaling completes
      const now = Date.now();
      window.__rtcNoPeerLogTs = window.__rtcNoPeerLogTs || 0;
      if (entries.length === 0) {
        if (now - window.__rtcNoPeerLogTs > 60000) {
          try { console.log('[RTC] No peer connections yet.'); } catch {}
          window.__rtcNoPeerLogTs = now;
        }
        return;
      }
      for (const [peerId, pc] of entries) {
        try {
          const stats = await pc.getStats();
          stats.forEach((report) => {
            if (report.type === 'outbound-rtp' && (report.kind === 'audio' || report.mediaType === 'audio')) {
              const level = report.audioLevel != null ? report.audioLevel : '(n/a)';
              const packets = report.packetsSent;
              const bytes = report.bytesSent;
              console.log('[RTC] outbound audio:', {
                peerId, // socket remote id
                ssrc: report.ssrc,
                packetsSent: packets,
                bytesSent: bytes,
                audioLevel: level,
                remoteRtpId: report.remoteId || null,
              });
              if ((packets === 0 || bytes === 0) && at && (!at.enabled || at.readyState !== 'live')) {
                console.log('[RTC] hint: Mic track is muted or stopped. Toggle microphone on and check browser permissions.');
              }
            }
          });
        } catch (e) {
          console.warn('[RTC] getStats failed', e && (e.message || e));
        }
      }
    } catch (e) {
      console.warn('[RTC] logOutgoingAudioLevel failed', e && (e.message || e));
    }
  }, []);

  // Watchdog: detect local outbound audio not actually sending (packets/bytes stay at 0)
  // and attempt automated repairs with exponential backoff until recovered.
  useEffect(() => {
    let timer = null;
    let currentDelay = 5000; // Start with 5s, will increase exponentially

    // Track repair state across ticks
    // repairing: actively attempting to fix
    // repairAttempts: how many attempts made
    // notified: whether final sticky warning was shown
    // lastNotifyAt: throttle transient notifications
    const ref = audioRemoteWatchRef.current || {};
    if (ref.repairing == null) ref.repairing = false;
    if (ref.repairAttempts == null) ref.repairAttempts = 0;
    if (ref.notified == null) ref.notified = false;
    if (ref.lastNotifyAt == null) ref.lastNotifyAt = 0;
    audioRemoteWatchRef.current = ref;

    const MAX_ATTEMPTS = 4; // Reduced from 5, with longer backoff
    const NOTIFY_THROTTLE_MS = 5000; // Increased to reduce notification spam
    const BASE_DELAY_MS = 5000;
    const MAX_DELAY_MS = 30000; // Cap at 30 seconds

    if (!joined) {
      // Reset when leaving the call
      audioRemoteWatchRef.current = { repairing: false, repairAttempts: 0, notified: false, lastNotifyAt: 0 };
      return;
    }

    const tick = async () => {
      try {
        const entries = Array.from(peersRef.current.entries());
        const connectedEntries = entries.filter(([_, pc]) => {
          try {
            const cs = pc.connectionState || '';
            const ics = pc.iceConnectionState || '';
            return cs === 'connected' || cs === 'completed' || ics === 'connected' || ics === 'completed';
          } catch { return false; }
        });
        if (connectedEntries.length === 0) {
          // Nothing to do if we have no connected peers
          ref.repairing = false;
          ref.repairAttempts = 0;
          ref.notified = false;
          return;
        }

        // If user intentionally disabled mic or we have no live local mic track, do not warn
        const at = localStreamRef.current?.getAudioTracks?.()[0] || null;
        if (!desiredMicEnabledRef.current || !at || at.enabled === false || at.readyState !== 'live') {
          ref.repairing = false;
          ref.repairAttempts = 0;
          ref.notified = false;
          return;
        }
        // Additionally, ensure we have an attached audio RTCRtpSender with a live track on any PC
        let hasLiveAudioSender = false;
        for (const [_, pc] of connectedEntries) {
          try {
            const senders = typeof pc.getSenders === 'function' ? pc.getSenders() : [];
            const s = senders.find(snd => snd && snd.track && snd.track.kind === 'audio' && snd.track.readyState === 'live' && snd.track.enabled !== false);
            if (s) { hasLiveAudioSender = true; break; }
          } catch {}
        }
        if (!hasLiveAudioSender) {
          // Try to repair immediately by wiring senders
          try { await ensureAudioSendersActive(at); } catch {}
          ref.repairing = false;
          ref.repairAttempts = 0;
          ref.notified = false;
          return;
        }

        // Check outbound audio stats across connected peers
        let sawOutboundAudio = false;
        let anyProgress = false;
        let anyRemoteLinked = false;

        for (const [_, pc] of connectedEntries) {
          try {
            const stats = await pc.getStats();
            stats.forEach((report) => {
              if (report.type === 'outbound-rtp' && (report.kind === 'audio' || report.mediaType === 'audio')) {
                sawOutboundAudio = true;
                const packets = Number(report.packetsSent || 0);
                const bytes = Number(report.bytesSent || 0);
                if (report.remoteId || report.remoteRtpId) anyRemoteLinked = true;
                if (packets > 0 || bytes > 0) anyProgress = true;
              }
            });
          } catch {}
          if (anyProgress) break;
        }

        // If we have no outbound audio stats yet, let things warm up without counting against repairs
        if (!sawOutboundAudio) {
          return;
        }

        // Healthy path recovered: announce success (if we were repairing)
        if (anyProgress && anyRemoteLinked) {
          if (ref.repairing || ref.repairAttempts > 0) {
            addNotification({ fallback: 'Audio path restored ✅', severity: 'success' });
          }
          ref.repairing = false;
          ref.repairAttempts = 0;
          ref.notified = false;
          return;
        }

        // Problem persists: attempt repair loop
        const now = Date.now();
        const showProgressNote = () => {
          if (now - (ref.lastNotifyAt || 0) > NOTIFY_THROTTLE_MS) {
            addNotification({
              fallback: `Fixing audio… attempt ${Math.min(ref.repairAttempts + 1, MAX_ATTEMPTS)}/${MAX_ATTEMPTS}`,
              severity: 'info'
            });
            ref.lastNotifyAt = now;
          }
        };

        // Keep trying up to MAX_ATTEMPTS before giving a sticky guidance to reload
        if (ref.repairAttempts < MAX_ATTEMPTS) {
          ref.repairing = true;
          ref.repairAttempts += 1;

          showProgressNote();

          try {
            // Ensure senders are properly wired and active
            const micTrack = localStreamRef.current?.getAudioTracks?.()[0] || null;
            if (micTrack && micTrack.readyState === 'live') {
              // Globally ensure sender paths exist and are active
              try { await ensureAudioSendersActive(micTrack); } catch {}

              // Also re-attach mic on each connected PC and restart ICE to refresh RTP bindings
              for (const [remoteId, pc] of connectedEntries) {
                try {
                  const sender = pc.getSenders().find(s => s.track && s.track.kind === 'audio');
                  if (sender) {
                    try { await sender.replaceTrack(micTrack); } catch {}
                    try {
                      const params = sender.getParameters?.() || {};
                      if (Array.isArray(params.encodings)) {
                        let changed = false;
                        params.encodings.forEach(e => { if (e && e.active === false) { e.active = true; changed = true; } });
                        if (changed) { try { await sender.setParameters(params); } catch {} }
                      }
                    } catch {}
                  } else {
                    // Ensure an audio transceiver exists to send on
                    try { pc.addTransceiver('audio', { direction: 'sendrecv' }); } catch {}
                  }
                  // Trigger ICE restart renegotiation to refresh RTP/DTLS
                  try { await makeOfferRef.current?.(remoteId, { iceRestart: true }); } catch {}
                } catch {}
              }
            }
          } catch {}

          // Optional: log once shortly after an attempt to aid debugging
          setTimeout(() => { try { logOutgoingAudioLevel().catch(()=>{}); } catch {} }, 2000);
          return;
        }

        // Exceeded attempts: one-time sticky guidance to reload
        if (!ref.notified) {
          ref.notified = true;
          addNotification({
            i18nKey: 'rtc.outboundAudioStuckReload',
            fallback: 'We could not establish the outgoing audio path. Please reload the page to refresh microphone permissions.',
            severity: 'warning',
            sticky: true
          });
        }
      } catch {}
    };

    // Use setTimeout with exponential backoff instead of fixed interval
    const scheduleNextTick = () => {
      timer = setTimeout(async () => {
        await tick();
        // Calculate next delay with exponential backoff
        const attempts = audioRemoteWatchRef.current?.repairAttempts || 0;
        currentDelay = Math.min(BASE_DELAY_MS * Math.pow(1.5, attempts), MAX_DELAY_MS);
        scheduleNextTick();
      }, currentDelay);
    };
    // fire one early after short delay to warm up
    setTimeout(() => { tick().then(scheduleNextTick); }, 3000);
    return () => { if (timer) clearTimeout(timer); };
  }, [joined, addNotification, ensureAudioSendersActive, logOutgoingAudioLevel]);

  // Watchdog: if joined but no peers discovered for a while, re-request participants and attempt a safe re-join once
  useEffect(() => {
    let interval = null;
    if (!joined) { try { noPeerRejoinRef.current.attempted = false; } catch {} return; }
    const tick = () => {
      try {
        const s = socketRef.current;
        const peersCount = peersRef.current?.size || 0;
        if (s && s.connected && peersCount === 0) {
          try { s.emit('request-participants'); } catch {}
          if (!noPeerRejoinRef.current.attempted && lastJoinParamsRef.current) {
            try { s.emit('join', lastJoinParamsRef.current); } catch {}
            noPeerRejoinRef.current.attempted = true;
          }
        }
      } catch {}
    };
    interval = setInterval(tick, 10000);
    // also run after short delay
    setTimeout(tick, 3000);
    return () => { if (interval) clearInterval(interval); };
  }, [joined]);

  // Internal helpers
  const getMeta = useCallback((remoteId) => {
    let m = metaRef.current.get(remoteId);
    if (!m) { m = { makingOffer: false, polite: false }; metaRef.current.set(remoteId, m); }
    return m;
  }, []);

  const decidePolite = useCallback((remoteId) => {
    try {
      // Deterministic: the socket with lexicographically higher id is polite
      if (!remoteId || !selfId) return false;
      const polite = String(selfId) > String(remoteId);
      const m = getMeta(remoteId);
      m.polite = polite;
      return polite;
    } catch { return false; }
  }, [selfId, getMeta]);

  // Media monitoring: sample per-peer stats for ICE/media health
  const getPeerStatSample = useCallback(async (peerId, isLocal) => {
    try {
      if (isLocal) {
        // Aggregate local outbound across all peer connections
        let audioOutboundPackets = 0;
        let videoOutboundPackets = 0;
        let audioRemoteRtpId = null;
        let videoRemoteRtpId = null;
        let transportReady = false;
        for (const [, pc] of peersRef.current.entries()) {
          try {
            // Consider transport ready if ICE or connection is connected/completed
            const ice = pc.iceConnectionState;
            const conn = pc.connectionState;
            if (ice === 'connected' || ice === 'completed' || conn === 'connected') {
              transportReady = true;
            }
          } catch {}
          try {
            const stats = await pc.getStats();
            stats.forEach((report) => {
              if (report.type === 'outbound-rtp') {
                const kind = report.kind || report.mediaType;
                if (kind === 'audio') {
                  audioOutboundPackets += report.packetsSent || 0;
                  if (!audioRemoteRtpId && report.remoteId) audioRemoteRtpId = report.remoteId;
                } else if (kind === 'video') {
                  videoOutboundPackets += report.packetsSent || 0;
                  if (!videoRemoteRtpId && report.remoteId) videoRemoteRtpId = report.remoteId;
                }
              }
            });
          } catch {}
        }
        return { audioOutboundPackets, videoOutboundPackets, audioRemoteRtpId, videoRemoteRtpId, transportReady };
      } else {
        const pc = peersRef.current.get(peerId);
        if (!pc) return null;
        let audioInboundPackets = 0;
        let videoInboundPackets = 0;
        const stats = await pc.getStats();
        stats.forEach((report) => {
          if (report.type === 'inbound-rtp') {
            const kind = report.kind || report.mediaType;
            if (kind === 'audio') {
              audioInboundPackets += report.packetsReceived || 0;
            } else if (kind === 'video') {
              videoInboundPackets += report.packetsReceived || 0;
            }
          }
        });
        return { audioInboundPackets, videoInboundPackets };
      }
    } catch {
      return null;
    }
  }, []);

  // --- Early-phase per-peer monitor utilities ---
  const MAX_ICE_RESTARTS = 2;
  const EARLY_WINDOW_MS = 15000; // monitor for first 15s after peer appears
  const MONITOR_INTERVAL_MS = 2500;

  const stopPeerMonitor = useCallback((remoteId) => {
    try {
      const diag = peerDiagnosticsRef.current.get(remoteId);
      if (diag && diag.timer) { try { clearInterval(diag.timer); } catch {} }
      peerDiagnosticsRef.current.delete(remoteId);
    } catch {}
  }, []);

  const checkPeerMedia = useCallback(async (remoteId) => {
    try {
      const pc = peersRef.current.get(remoteId);
      if (!pc) { stopPeerMonitor(remoteId); return true; }
      const now = Date.now();
      let diag = peerDiagnosticsRef.current.get(remoteId);
      if (!diag) { diag = { startedAt: now, iceFailures: 0, attempts: 0, connectedOnce: false, firstRemoteMediaAt: 0, lastState: '' }; peerDiagnosticsRef.current.set(remoteId, diag); }

      const ice = pc.iceConnectionState;
      const conn = pc.connectionState;
      if (ice === 'connected' || ice === 'completed' || conn === 'connected') {
        diag.connectedOnce = true;
      }

      // If early failure/disconnect, attempt limited ICE restarts
      const withinEarly = (now - (diag.startedAt || now)) <= EARLY_WINDOW_MS;
      if (withinEarly && (ice === 'failed' || ice === 'disconnected')) {
        if ((diag.iceFailures || 0) < MAX_ICE_RESTARTS) {
          diag.iceFailures = (diag.iceFailures || 0) + 1;
          try {
            addNotification({ fallback: `Connection unstable with peer ${remoteId}. Attempting ICE restart (${diag.iceFailures}/${MAX_ICE_RESTARTS})...`, severity: 'warning' });
          } catch {}
          try { await makeOfferRef.current?.(remoteId, { iceRestart: true }); } catch {}
        } else {
          try {
            addNotification({ fallback: `ICE connection to peer ${remoteId} failed repeatedly. Please check network or try rejoining.`, severity: 'error' });
          } catch {}
        }
      }

      // Detect first remote media via participants stream or inbound RTP stats
      try {
        const p = participants?.[remoteId];
        const hasStreamTrack = !!(p?.stream && ((p.stream.getTracks?.() || []).some(t => t.readyState === 'live')));
        if (hasStreamTrack && !diag.firstRemoteMediaAt) {
          diag.firstRemoteMediaAt = now;
        }
      } catch {}

      try {
        const sample = await getPeerStatSample(remoteId, false);
        const inbound = (sample?.audioInboundPackets || 0) + (sample?.videoInboundPackets || 0);
        if (inbound > 0 && !diag.firstRemoteMediaAt) { diag.firstRemoteMediaAt = now; }
      } catch {}

      // If ICE connected but no remote media after grace, trigger a soft renegotiation once
      if (diag.connectedOnce && (ice === 'connected' || ice === 'completed' || conn === 'connected')) {
        const hasMedia = !!diag.firstRemoteMediaAt;
        const waited = now - (diag.startedAt || now);
        if (!hasMedia && waited > 8000 && (diag.renegAttempted !== true)) {
          diag.renegAttempted = true;
          try { addNotification({ fallback: `No media received yet from peer ${remoteId}. Trying to renegotiate...`, severity: 'info' }); } catch {}
          try { await makeOfferRef.current?.(remoteId); } catch {}
        }
        // Stop monitor if media flowing or after early window has passed without further issues
        if (hasMedia || waited > EARLY_WINDOW_MS) {
          stopPeerMonitor(remoteId);
          return true;
        }
      }

      return false;
    } catch {
      return false;
    }
  }, [participants, getPeerStatSample, addNotification]);

  const startPeerMonitor = useCallback((remoteId) => {
    try { stopPeerMonitor(remoteId); } catch {}
    const diag = { startedAt: Date.now(), iceFailures: 0, attempts: 0, connectedOnce: false, firstRemoteMediaAt: 0, lastState: '' };
    peerDiagnosticsRef.current.set(remoteId, diag);
    const timer = setInterval(() => { try { checkPeerMedia(remoteId); } catch {} }, MONITOR_INTERVAL_MS);
    diag.timer = timer;
    // Run an early check soon after creation
    setTimeout(() => { try { checkPeerMedia(remoteId); } catch {} }, 1200);
  }, [checkPeerMedia, stopPeerMonitor]);

  const createPeerConnection = useCallback(async (remoteId) => {
    if (peersRef.current.has(remoteId)) return peersRef.current.get(remoteId);
    const pc = new RTCPeerConnection(iceServers);
    peersRef.current.set(remoteId, pc);

    try { console.log('[RTC][', remoteId, '] Using ICE servers:', iceServers); } catch {}

    // Verbose ICE/signaling logging for diagnostics
    try {
      pc.addEventListener('iceconnectionstatechange', () => {
        try { console.log('[RTC][', remoteId, '] iceConnectionState =', pc.iceConnectionState); } catch {}
        try {
          const state = pc.iceConnectionState;
          if (state === 'connected' || state === 'completed') {
            const diag = peerDiagnosticsRef.current.get(remoteId);
            if (diag) diag.connectedOnce = true;
            try { setTimeout(() => { try { checkPeerMedia(remoteId); } catch {} }, 200); } catch {}
          }
        } catch {}
      });
      pc.addEventListener('connectionstatechange', () => {
        try { console.log('[RTC][', remoteId, '] connectionState =', pc.connectionState); } catch {}
      });
      pc.addEventListener('signalingstatechange', () => {
        try { console.log('[RTC][', remoteId, '] signalingState =', pc.signalingState); } catch {}
      });
    } catch {}

    // Add local tracks: if screen sharing active, use its video track instead of camera
    const local = localStreamRef.current;
    const screen = screenStreamRef.current;
    // Track what kinds we have locally so we add recvonly transceivers for missing kinds.
    let hasLocalAudio = false;
    let hasLocalVideo = false;
    if (local || screen) {
      // Audio: always from local mic if available
      if (local) {
        const audios = local.getAudioTracks();
        audios.forEach(t => pc.addTrack(t, local));
        if (audios.length) hasLocalAudio = true;
      }
      // Video: prefer screen track if active
      const screenV = screen?.getVideoTracks?.()[0] || null;
      if (screenV) {
        pc.addTrack(screenV, screen);
        hasLocalVideo = true;
      } else if (local) {
        const videos = local.getVideoTracks();
        videos.forEach(t => pc.addTrack(t, local));
        if (videos.length) hasLocalVideo = true;
      }
    }
    // If virtual background active, replace sender with processed track for this pc
    try {
      const vbTrack = vbRef.current?.outputStream?.getVideoTracks?.()[0] || null;
      if (vbTrack) {
        const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
        if (sender) { try { sender.replaceTrack(vbTrack); } catch {} }
      }
    } catch {}
    // Important: ensure our initial offer contains m-lines for both audio and video so
    // peers can send us media even if we don't publish a track of that kind yet.
    try { if (!hasLocalAudio) pc.addTransceiver('audio', { direction: 'recvonly' }); } catch {}
    try { if (!hasLocalVideo) pc.addTransceiver('video', { direction: 'recvonly' }); } catch {}

    // Ensure audio sender exists and is marked active for any initial mic track present
    try { ensureAudioSendersActive(); } catch {}

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        try { console.log('[RTC][', remoteId, '] got candidate', e.candidate.candidate); } catch {}
        socketRef.current?.emit('ice-candidate', { to: remoteId, candidate: e.candidate });
      } else {
        try { console.log('[RTC][', remoteId, '] ICE candidate gathering complete'); } catch {}
      }
    };

    // Ensure we have a hidden sink element for iOS autoplay/ringer mute workaround
    const getOrCreateRemoteSink = (id) => {
      let el = remoteSinksRef.current.get(id);
      if (!el) {
        el = document.createElement('video');
        el.autoplay = true;
        el.muted = false;
        el.playsInline = true;
        el.setAttribute('webkit-playsinline', 'true');
        el.style.width = '1px';
        el.style.height = '1px';
        el.style.position = 'fixed';
        el.style.left = '-9999px';
        try { document.body.appendChild(el); } catch {}
        // Apply chosen audio output device if supported
        try {
          const sinkId = selectedDevices.audioOutputId || 'default';
          if (typeof el.setSinkId === 'function') { el.setSinkId(sinkId).catch(()=>{}); }
        } catch {}
        remoteSinksRef.current.set(id, el);
      }
      return el;
    };

    pc.ontrack = (e) => {
      const [stream] = e.streams;
      setParticipants(prev => {
        const existing = prev[remoteId] || {};
        return { ...prev, [remoteId]: { id: remoteId, name: existing.name || '', stream } };
      });
      // Route remote audio to hidden sink for autoplay on iOS
      try {
        const sink = getOrCreateRemoteSink(remoteId);
        sink.srcObject = stream || new MediaStream([e.track]);
        const tryPlay = () => { try { sink.play().catch(()=>{}); } catch {} };
        try { e.track && (e.track.onunmute = tryPlay); } catch {}
        tryPlay();
      } catch {}
      // Mark that we received first remote media and stop early-phase monitor if running
      try {
        const diag = peerDiagnosticsRef.current.get(remoteId);
        if (diag && !diag.firstRemoteMediaAt) {
          diag.firstRemoteMediaAt = Date.now();
          try { stopPeerMonitor(remoteId); } catch {}
        }
      } catch {}
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed' || pc.connectionState === 'closed') {
        closePeer(remoteId);
      }
    };

    // Negotiate when needed, with guard
    pc.onnegotiationneeded = async () => {
      try { setTimeout(() => { try { makeOfferRef.current && makeOfferRef.current(remoteId).catch(()=>{}); } catch {} }, 0); } catch {}
    };

    try { startPeerMonitor(remoteId); } catch {}
    return pc;
  }, [iceServers, selectedDevices.audioOutputId, ensureAudioSendersActive, startPeerMonitor]);

  // Convenience: map of id -> MediaStream for remote (and self) participants
  const remoteStreams = useMemo(() => {
    const out = {};
    try {
      Object.entries(participants || {}).forEach(([id, p]) => {
        if (p && p.stream) out[id] = p.stream;
      });
    } catch {}
    return out;
  }, [participants]);

  const makeOffer = useCallback(async (remoteId, opts = {}) => {
    const pc = await createPeerConnection(remoteId);
    const meta = getMeta(remoteId);
    if (pc.signalingState !== 'stable') {
      // Defer if not stable to avoid state errors; queue a retry shortly to recover from glare
      try {
        setTimeout(() => { try { makeOfferRef.current && makeOfferRef.current(remoteId, opts).catch(()=>{}); } catch {} }, 250);
      } catch {}
      return;
    }
    if (meta.makingOffer) return;
    meta.makingOffer = true;
    try {
      const base = { offerToReceiveAudio: true, offerToReceiveVideo: true };
      const options = opts && opts.iceRestart ? { ...base, iceRestart: true } : base;
      const offer = await pc.createOffer(options);
      await pc.setLocalDescription(offer);
      socketRef.current?.emit('offer', { to: remoteId, description: pc.localDescription });
    } catch (e) {
      try { console.warn('[RTC] makeOffer failed for', remoteId, e && (e.message || e)); } catch {}
    } finally {
      meta.makingOffer = false;
    }
  }, [createPeerConnection, getMeta]);

  // Bind the up-to-date makeOffer implementation to the ref to avoid TDZ in earlier hooks
  useEffect(() => { makeOfferRef.current = makeOffer; }, [makeOffer]);

  // Utility: attach/replace a mic track on a specific peer connection by remoteId
  const useMicOnPeer = useCallback(async (remoteId, stream) => {
    if (!remoteId || !stream) return false;
    const track = stream.getAudioTracks()?.[0] || null;
    if (!track) return false;
    const pc = await createPeerConnection(remoteId);
    try {
      const txs = typeof pc.getTransceivers === 'function' ? pc.getTransceivers() : [];
      const audioTx = txs.find(tx => (tx?.receiver?.track?.kind === 'audio') || (tx?.sender?.track?.kind === 'audio')) || null;
      if (audioTx && audioTx.sender) {
        try { typeof audioTx.setDirection === 'function' ? audioTx.setDirection('sendrecv') : (audioTx.direction = 'sendrecv'); } catch {}
        await audioTx.sender.replaceTrack(track);
      } else {
        const sender = pc.getSenders().find(s => s.track && s.track.kind === 'audio');
        if (sender) {
          await sender.replaceTrack(track);
        } else {
          try { pc.addTransceiver('audio', { direction: 'sendrecv' }); } catch {}
          pc.addTrack(track, stream);
        }
      }
      return true;
    } catch {
      return false;
    }
  }, [createPeerConnection]);

  const closePeer = useCallback((remoteId) => {
    const pc = peersRef.current.get(remoteId);
    try { stopPeerMonitor(remoteId); } catch {}
    if (pc) {
      try { pc.ontrack = null; } catch {}
      try { pc.close(); } catch {}
      peersRef.current.delete(remoteId);
    }
    // Remove hidden sink element if exists
    try {
      const el = remoteSinksRef.current.get(remoteId);
      if (el && el.parentNode) { el.srcObject = null; el.parentNode.removeChild(el); }
      remoteSinksRef.current.delete(remoteId);
    } catch {}
    // Clear meta
    try { metaRef.current.delete(remoteId); } catch {}
    setParticipants(prev => {
      const copy = { ...prev };
      delete copy[remoteId];
      return copy;
    });
  }, []);

  async function stopVirtualBackground() {
    try {
      const vb = vbRef.current;
      if (vb && vb.processor) {
        try { vb.processor.stop(); } catch {}
      }
    } catch {}
    vbRef.current = { processor: null, outputStream: null };
    // Restore local preview to original local stream
    try {
      const ls = localStreamRef.current;
      if (ls) setParticipants(prev => ({ ...prev, self: { ...(prev.self || { id: 'self', name: displayName || 'Me' }), id: 'self', name: displayName || 'Me', stream: ls, isSelf: true } }));
    } catch {}
    // Replace outgoing sender back to original cam track if any
    try {
      const camTrack = localStreamRef.current?.getVideoTracks?.()[0] || null;
      if (camTrack && camTrack.readyState === 'live') {
        const replacePromises = [];
        peersRef.current.forEach((pc, remoteId) => {
          try {
            const connState = pc.connectionState || '';
            if (connState === 'closed' || connState === 'failed') return;
            const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
            if (sender) {
              replacePromises.push(
                sender.replaceTrack(camTrack).catch(e => {
                  console.warn('[RTC] stopVirtualBackground replaceTrack failed for peer', remoteId, e?.message || e);
                })
              );
            }
          } catch {}
        });
        if (replacePromises.length > 0) {
          await Promise.allSettled(replacePromises);
        }
      }
    } catch {}
  }

  async function startVirtualBackground(effect = 'blur') {
    if (screenStreamRef.current) return; // don't apply during screen share
    const src = localStreamRef.current;
    if (!src) return;
    const vTrack = src.getVideoTracks?.()[0] || null;
    if (!vTrack) return;
    try {
      const processor = await createVirtualBackgroundProcessor({ sourceStream: src, type: effect });
      processor.setType(effect);
      const out = processor.outputStream;
      processor.start();
      vbRef.current = { processor, outputStream: out };
      const processedTrack = out.getVideoTracks()?.[0] || null;
      if (!processedTrack) return;
      // Replace outgoing video sender track - properly await all replacements
      const replacePromises = [];
      peersRef.current.forEach((pc, remoteId) => {
        try {
          const connState = pc.connectionState || '';
          if (connState === 'closed' || connState === 'failed') return;
          const sender = pc.getSenders().find(s => s.track && s.track.kind === 'video');
          if (sender) {
            replacePromises.push(
              sender.replaceTrack(processedTrack).catch(e => {
                console.warn('[RTC] startVirtualBackground replaceTrack failed for peer', remoteId, e?.message || e);
              })
            );
          }
        } catch {}
      });
      if (replacePromises.length > 0) {
        await Promise.allSettled(replacePromises);
      }
      // Update local preview to show processed video but keep original audio
      const composed = new MediaStream([
        ...((localStreamRef.current?.getAudioTracks?.() || [])),
        processedTrack
      ]);
      setParticipants(prev => ({ ...prev, self: { ...(prev.self || { id: 'self', name: displayName || 'Me' }), id: 'self', name: displayName || 'Me', stream: composed, isSelf: true } }));
    } catch (e) {
      try { console.warn('[RTC] Virtual background failed to start:', e && (e.message || e)); } catch {}
      addNotification({ i18nKey: 'rtc.virtualBackgroundFailed', fallback: 'Background effect failed to start. Using original camera.', severity: 'warning' });
      await stopVirtualBackground();
    }
  }

  async function applyBackgroundEffect(effect) {
    if (!effect || effect === 'none') {
      await stopVirtualBackground();
      return;
    }
    await startVirtualBackground(effect);
  }

  // Best-effort: attempt to revoke camera/microphone permissions (not supported in all browsers)
  async function bestEffortRevokePermissions() {
    try {
      const perms = navigator?.permissions;
      if (!perms || typeof perms.revoke !== 'function') return;
      try { await perms.revoke({ name: 'microphone' }); } catch {}
      try { await perms.revoke({ name: 'camera' }); } catch {}
    } catch {}
  }

  function cleanup() {
    // Stop virtual background if running
    try { /* fire-and-forget */ stopVirtualBackground(); } catch {}
    // Close and teardown all peer connections and senders
    for (const [_, pc] of peersRef.current.entries()) {
      try {
        const senders = typeof pc.getSenders === 'function' ? pc.getSenders() : [];
        senders.forEach(snd => { try { snd.replaceTrack(null); } catch {} });
      } catch {}
      try { pc.ontrack = null; } catch {}
      try { pc.close(); } catch {}
    }
    peersRef.current.clear();
    // Stop all early-phase peer monitors
    try {
      for (const diag of peerDiagnosticsRef.current.values()) {
        try { if (diag && diag.timer) clearInterval(diag.timer); } catch {}
      }
      peerDiagnosticsRef.current.clear();
    } catch {}
    // Remove all hidden remote sinks
    try {
      for (const el of remoteSinksRef.current.values()) {
        try { if (el && el.parentNode) { el.srcObject = null; el.parentNode.removeChild(el); } } catch {}
      }
      remoteSinksRef.current.clear();
    } catch {}
    // Disconnect signaling socket
    const s = socketRef.current;
    if (s) { try { s.removeAllListeners?.(); } catch {} try { s.disconnect(); } catch {} }
    socketRef.current = null;
    // Stop all local media tracks (camera/mic)
    const stream = localStreamRef.current;
    if (stream) { try { stream.getTracks().forEach(t => t.stop()); } catch {} }
    localStreamRef.current = null;
    // Stop any active screen share tracks
    const ss = screenStreamRef.current;
    if (ss) { try { ss.getTracks().forEach(t => t.stop()); } catch {} }
    screenStreamRef.current = null;
    // Best-effort: remove devicechange listener to avoid leaks (safe even if not attached)
    try { navigator.mediaDevices?.removeEventListener?.('devicechange', refreshDevices); } catch {}
    // Reset desired media toggles so next join starts with enabled mic/cam
    try { desiredMicEnabledRef.current = true; } catch {}
    try { desiredCamEnabledRef.current = true; } catch {}
    // Reset simple UI flags so UI reflects that no devices are in use
    try { setMicEnabled(false); } catch {}
    try { setCameraEnabled(false); } catch {}
    try { setScreenSharing(false); } catch {}
    // Clear saved device selections so next call re-prompts cleanly
    try { localStorage.removeItem('rtc.devicePrefs'); } catch {}
    try { setSelectedDevices({ audioInputId: null, videoInputId: null, audioOutputId: null }); } catch {}
    // Also clear devices snapshot to hide labels until re-authorized
    try { setDevices({ audioInputs: [], videoInputs: [], audioOutputs: [], defaultAudioInputLabel: '', defaultAudioOutputLabel: '' }); } catch {}
    // Clear meta and UI state
    try { metaRef.current.clear(); } catch {}
    setParticipants({});
    setJoined(false);
    setScreenSharerId(null);
    setHostId(null);
    setSelfId(null);
    setEndedByHost(false);
    lastJoinParamsRef.current = null;
    try { audioRemoteWatchRef.current = { repairing: false, repairAttempts: 0, notified: false, lastNotifyAt: 0 }; } catch {}

    // Fire-and-forget: best‑effort revoke permissions (if browser supports it)
    try { bestEffortRevokePermissions(); } catch {}
  }

  const setBackgroundEffect = useCallback(async (effect) => {
    const allowed = new Set(['none','blur','blur-light','blur-strong','image-1','image-2','image-3','image-4','image-5','image-6','image-7','image-8','image-9','image-10','image-11','image-12']);
    const eff = allowed.has(effect) ? effect : 'none';
    setBackgroundEffectState(eff);
    try {
      if (localStreamRef.current && (localStreamRef.current.getVideoTracks?.() || []).length > 0) {
        await applyBackgroundEffect(eff);
      }
    } catch {}
    return eff;
  }, []);

  // Permissions preflight: check current browser status for mic/camera (best-effort)
  const checkPermissions = useCallback(async () => {
    const out = { microphone: 'unknown', camera: 'unknown', labelsVisible: false, supported: true };
    try {
      const any = navigator?.permissions?.query;
      if (!any) { out.supported = false; }
      if (any) {
        try { out.microphone = (await navigator.permissions.query({ name: 'microphone' })).state; } catch {}
        try { out.camera = (await navigator.permissions.query({ name: 'camera' })).state; } catch {}
      }
      try {
        const list = await navigator.mediaDevices?.enumerateDevices?.();
        out.labelsVisible = Array.isArray(list) ? list.some(d => d.label) : false;
      } catch {}
    } catch {}
    return out;
  }, []);

  // Permissions request: prompt for mic/cam and immediately release
  const requestPermissions = useCallback(async (kind = 'both') => {
    const needAudio = kind === 'both' || kind === 'audio';
    const needVideo = kind === 'both' || kind === 'video';
    let okAudio = false, okVideo = false;
    try {
      const { stream } = await getUserMediaSafe({ audio: needAudio || false, video: needVideo || false });
      if (stream) {
        try {
          if (needAudio && (stream.getAudioTracks?.().length || 0) > 0) okAudio = true;
          if (needVideo && (stream.getVideoTracks?.().length || 0) > 0) okVideo = true;
        } catch {}
        try { stream.getTracks().forEach(t => t.stop()); } catch {}
      }
    } catch (e) {
      // Best-effort: map errors to booleans
      if (needAudio) okAudio = false;
      if (needVideo) okVideo = false;
    }
    return { okAudio, okVideo };
  }, []);

  // Manual repair: try to rewire mic senders and ICE-restart all peers now
  const repairAudioPath = useCallback(async () => {
    try {
      const micTrack = getLocalMicTrack();
      await ensureAudioSendersActive(micTrack);
      for (const remoteId of peersRef.current.keys()) {
        try { await makeOfferRef.current?.(remoteId, { iceRestart: true }); } catch {}
      }
      addNotification({ fallback: 'Attempted to repair audio path (re-attached mic and restarted ICE).', severity: 'info' });
    } catch {}
  }, [ensureAudioSendersActive, getLocalMicTrack, addNotification]);

  // Explicit action to best-effort revoke permissions (optional; cleanup already calls this)
  const clearPermissions = useCallback(async () => {
    try { await bestEffortRevokePermissions(); } catch {}
  }, []);

  // Media monitoring: hook driven polling per peer (local + remotes)
  const peerIds = useMemo(() => {
    const remoteIds = Object.keys(participants || {});
    return selfId ? [selfId, ...remoteIds] : remoteIds;
  }, [participants, selfId]);

  const mediaIssues = usePeerMediaIssues({
    peerIds,
    localPeerId: selfId || '',
    pollIntervalMs: 2000,
    getPeerStatSample,
  });

  // Notify on degraded/failed transitions once per status change per peer
  const lastIssueStatusRef = useRef(new Map());
  useEffect(() => {
    try {
      for (const issue of mediaIssues) {
        const prev = lastIssueStatusRef.current.get(issue.peerId);
        if (prev !== issue.status) {
          lastIssueStatusRef.current.set(issue.peerId, issue.status);
          if (issue.status === 'degraded' || issue.status === 'failed') {
            const who = issue.peerId === selfId ? 'Your media' : `Peer ${issue.peerId}`;
            const txt = `${who} is ${issue.status}. Reasons: ${issue.reasons.join(', ') || 'unknown'}`;
            addNotification({ fallback: txt, severity: issue.status === 'failed' ? 'error' : 'warning' });
            try { console.warn('[RTC][monitor]', issue); } catch {}
          }
        }
      }
    } catch {}
  }, [mediaIssues, selfId, addNotification]);

  const join = useCallback(async () => {
    // Do NOT start local media here. Permissions should only be requested explicitly by the caller (e.g., when user clicks Join).
    try { await unlockIOSAudio(); } catch {}
    const payload = { roomId, displayName, groupName };
    lastJoinParamsRef.current = payload;
    socketRef.current?.emit('join', payload);
  }, [roomId, displayName, groupName]);

  return {
    state: {
      joined,
      participants,
      hostId,
      selfId,
      screenSharerId,
      chatMessages,
      notifications,
      roomGroupName,
      endedByHost,
      devices,
      selectedDevices,
      micEnabled,
      cameraEnabled,
      screenSharing,
      // expose the local screen share MediaStream when you are the sharer
      screenStream: screenStreamRef.current || null,
      remoteStreams,
      backgroundEffect,
      // expose peer connections for diagnostics/monitoring
      peerConnections: peersRef.current,
      mediaIssues,
    },
    actions: {
      join,
      leave,
      endCall,
      startLocalMedia,
      toggleMic,
      toggleCamera,
      startScreenShare,
      stopScreenShare,
      sendChat,
      removeNotification,
      refreshDevices,
      setAudioInput,
      setAudioOutput,
      logOutgoingAudioLevel,
      setBackgroundEffect,
      useMicOnPeer,
      // new:
      checkPermissions,
      requestPermissions,
      repairAudioPath,
      clearPermissions, // new explicit action to best-effort revoke mic/cam permissions
    }
  };
}